import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  ArrowRight,
  ArrowLeft,
  Crown,
  Shield,
  Globe,
  Briefcase,
  Building2,
  TrendingUp,
  CheckCircle,
  Loader2,
  Lock,
  MapPin,
  Wallet,
} from 'lucide-react';
import { z } from 'zod';

const emailSchema = z.string().trim().email().max(255);
const phoneSchema = z.string().optional();

interface AssessmentStep {
  id: string;
  question: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  options: { value: string; label: string; sublabel?: string }[];
}

const steps: AssessmentStep[] = [
  {
    id: 'investment-goal',
    question: 'What is your primary investment objective?',
    subtitle: 'Understanding your goals helps us identify the perfect opportunities',
    icon: Briefcase,
    options: [
      { value: 'capital-growth', label: 'Capital Appreciation', sublabel: 'Long-term value growth' },
      { value: 'rental-yield', label: 'Rental Income', sublabel: 'Consistent cash flow' },
      { value: 'lifestyle', label: 'Lifestyle & Legacy', sublabel: 'Personal use & inheritance' },
      { value: 'diversification', label: 'Portfolio Diversification', sublabel: 'Cross-border risk mitigation' },
      { value: 'tax-optimization', label: 'Tax Optimization', sublabel: 'Structuring for fiscal efficiency' },
      { value: 'relocation', label: 'Relocation & Residency', sublabel: 'Golden visa & citizenship pathways' },
      { value: 'asset-protection', label: 'Asset Protection', sublabel: 'Wealth preservation & privacy' },
      { value: 'exit-strategy', label: 'Political Hedging', sublabel: 'Secure base in stable jurisdiction' },
    ],
  },
  {
    id: 'markets',
    question: 'Which markets interest you most?',
    subtitle: 'Select all regions you\'d consider for investment',
    icon: Globe,
    options: [
      { value: 'monaco', label: 'Monaco', sublabel: 'Tax efficiency & prestige' },
      { value: 'dubai', label: 'Dubai & Abu Dhabi', sublabel: 'Growth & tax-free living' },
      { value: 'london', label: 'London', sublabel: 'Established luxury market' },
      { value: 'italy', label: 'Italy', sublabel: 'Heritage & lifestyle' },
      { value: 'spain', label: 'Spain', sublabel: 'Mediterranean living' },
      { value: 'france', label: 'France', sublabel: 'Côte d\'Azur & Paris' },
      { value: 'hong-kong', label: 'Hong Kong & Macau', sublabel: 'Asia\'s financial hubs' },
      { value: 'usa', label: 'USA', sublabel: 'NYC, LA & Miami' },
      { value: 'ksa', label: 'Saudi Arabia', sublabel: 'Vision 2030 opportunities' },
      { value: 'switzerland', label: 'Switzerland', sublabel: 'Alpine prestige & privacy' },
      { value: 'australia', label: 'Australia', sublabel: 'Harbour & coastal living' },
      { value: 'qatar', label: 'Qatar', sublabel: 'Emerging luxury market' },
    ],
  },
  {
    id: 'property-type',
    question: 'What type of property are you seeking?',
    subtitle: 'Each property type offers unique investment characteristics',
    icon: Building2,
    options: [
      { value: 'penthouse', label: 'Penthouse', sublabel: 'Exclusive city living' },
      { value: 'villa', label: 'Villa / Estate', sublabel: 'Privacy & space' },
      { value: 'waterfront', label: 'Waterfront Property', sublabel: 'Premium locations' },
      { value: 'chateau', label: 'Château / Historic', sublabel: 'Heritage & character' },
      { value: 'new-development', label: 'Off-Plan / New Build', sublabel: 'Early-stage pricing' },
      { value: 'any', label: 'Open to Opportunities', sublabel: 'Best value available' },
    ],
  },
  {
    id: 'timeline',
    question: 'What is your investment timeline?',
    subtitle: 'This helps us prioritize the most relevant opportunities',
    icon: TrendingUp,
    options: [
      { value: 'immediate', label: 'Ready to Invest', sublabel: 'Within 30 days' },
      { value: 'short', label: '1-3 Months', sublabel: 'Active search' },
      { value: 'medium', label: '3-6 Months', sublabel: 'Exploring options' },
      { value: 'long', label: '6+ Months', sublabel: 'Strategic planning' },
    ],
  },
  {
    id: 'budget',
    question: 'What is your investment range?',
    subtitle: 'All discussions are held in strict confidence',
    icon: Wallet,
    options: [
      { value: '2-5m', label: '€2M - €5M', sublabel: 'Entry luxury tier' },
      { value: '5-10m', label: '€5M - €10M', sublabel: 'Premium selection' },
      { value: '10-25m', label: '€10M - €25M', sublabel: 'Exclusive properties' },
      { value: '25-50m', label: '€25M - €50M', sublabel: 'Trophy assets' },
      { value: '50m+', label: '€50M+', sublabel: 'Ultra-prime & estates' },
    ],
  },
];

interface InvestmentAssessmentProps {
  variant?: 'inline' | 'modal';
  onComplete?: () => void;
}

const InvestmentAssessment = ({ variant = 'inline', onComplete }: InvestmentAssessmentProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactInfo, setContactInfo] = useState({ name: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const multiSelectSteps = ['markets', 'investment-goal'];

  const handleOptionSelect = (stepId: string, value: string) => {
    // For markets and investment-goal, allow multiple selection
    if (multiSelectSteps.includes(stepId)) {
      const current = (answers[stepId] as string[]) || [];
      if (current.includes(value)) {
        setAnswers({ ...answers, [stepId]: current.filter((m) => m !== value) });
      } else {
        setAnswers({ ...answers, [stepId]: [...current, value] });
      }
    } else {
      setAnswers({ ...answers, [stepId]: value });
      // Auto-advance after selection
      setTimeout(() => {
        if (currentStep < steps.length - 1) {
          setCurrentStep(currentStep + 1);
        } else {
          setShowContactForm(true);
        }
      }, 400);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowContactForm(true);
    }
  };

  const handleBack = () => {
    if (showContactForm) {
      setShowContactForm(false);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailValidation = emailSchema.safeParse(contactInfo.email);
    if (!emailValidation.success) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!contactInfo.name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setIsSubmitting(true);

    try {
      // Format the assessment answers into a readable message
      const assessmentSummary = Object.entries(answers)
        .map(([key, value]) => {
          const step = steps.find((s) => s.id === key);
          const stepName = step?.question || key;
          const valueStr = Array.isArray(value) ? value.join(', ') : value;
          return `${stepName}: ${valueStr}`;
        })
        .join('\n');

      const message = `Investment Assessment Results:\n\n${assessmentSummary}`;

      const { error } = await supabase.functions.invoke('submit-lead', {
        body: {
          name: contactInfo.name,
          email: emailValidation.data,
          phone: contactInfo.phone || undefined,
          message,
          source: 'investment-assessment',
          property_interest: (answers.markets as string[])?.join(', ') || 'Not specified',
        },
      });

      if (error) throw error;

      setIsComplete(true);
      toast.success('Thank you! We\'ll be in touch within 24 hours.');
      onComplete?.();
    } catch (error) {
      console.error('Assessment submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const progress = ((currentStep + 1) / (steps.length + 1)) * 100;
  const currentStepData = steps[currentStep];
  const StepIcon = currentStepData?.icon || Crown;

  const getMatchedSpecialist = () => {
    const selectedMarkets = (answers.markets as string[]) || [];
    const specialistMap: Record<string, { name: string; title: string; region: string; markets: string[] }> = {
      'europe': { name: 'Agota Giedrė', title: 'Senior Partner — European Markets', region: 'Monaco, London, Italy, Spain, France, Switzerland', markets: ['monaco', 'london', 'italy', 'spain', 'france', 'switzerland'] },
      'gulf': { name: 'Dalius Mišeikis', title: 'Managing Director — Gulf & MENA', region: 'Dubai, Abu Dhabi, KSA, Qatar', markets: ['dubai', 'ksa', 'qatar'] },
      'apac': { name: 'Viktoras Pranckūnas', title: 'Director — Asia-Pacific', region: 'Hong Kong, Macau, Australia', markets: ['hong-kong', 'australia'] },
      'americas': { name: 'Agota Giedrė', title: 'Senior Partner — Transatlantic', region: 'USA & Global', markets: ['usa'] },
    };

    for (const [, spec] of Object.entries(specialistMap)) {
      if (selectedMarkets.some(m => spec.markets.includes(m))) return spec;
    }
    return specialistMap['europe'];
  };

  const getBudgetLabel = () => {
    const budget = answers.budget as string;
    const map: Record<string, string> = { '2-5m': '€2M–€5M', '5-10m': '€5M–€10M', '10-25m': '€10M–€25M', '25-50m': '€25M–€50M', '50m+': '€50M+' };
    return map[budget] || budget;
  };

  if (isComplete) {
    const specialist = getMatchedSpecialist();
    const selectedMarkets = (answers.markets as string[]) || [];
    const marketLabels = steps[1].options.filter(o => selectedMarkets.includes(o.value)).map(o => o.label);

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-secondary to-background border border-gold/30 p-8 md:p-12"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-gold/10 via-transparent to-transparent" />
        <div className="relative z-10">
          {/* Success icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="w-16 h-16 rounded-full bg-gradient-gold flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="h-8 w-8 text-cream" />
          </motion.div>

          <h3 className="font-display text-2xl md:text-3xl text-foreground mb-2 text-center">
            Your Strategy is Being Prepared
          </h3>
          <p className="text-muted-foreground text-center text-sm mb-8">
            Based on your profile, we've matched you with the ideal advisor.
          </p>

          {/* Matched specialist card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-xl border border-gold/20 bg-background/60 p-5 mb-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <Crown className="h-4 w-4 text-gold" />
              <span className="text-xs font-medium text-gold uppercase tracking-wider">Your Assigned Advisor</span>
            </div>
            <p className="font-display text-lg text-foreground">{specialist.name}</p>
            <p className="text-sm text-muted-foreground mb-3">{specialist.title}</p>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3 text-gold/60" />
              <span>{specialist.region}</span>
            </div>
          </motion.div>

          {/* Investment summary */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-2 gap-3 mb-6"
          >
            <div className="rounded-lg bg-muted/20 border border-border/30 p-3">
              <p className="text-xs text-muted-foreground mb-1">Target Markets</p>
              <p className="text-sm font-medium text-foreground">{marketLabels.slice(0, 3).join(', ')}{marketLabels.length > 3 ? ` +${marketLabels.length - 3}` : ''}</p>
            </div>
            <div className="rounded-lg bg-muted/20 border border-border/30 p-3">
              <p className="text-xs text-muted-foreground mb-1">Investment Range</p>
              <p className="text-sm font-medium text-foreground">{getBudgetLabel()}</p>
            </div>
          </motion.div>

          {/* Next steps */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center"
          >
            <p className="text-sm text-muted-foreground mb-4">
              {specialist.name.split(' ')[0]} will contact you within <span className="text-gold font-medium">24 hours</span> with tailored recommendations for {contactInfo.name}.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Shield className="h-3.5 w-3.5 text-gold/60" />
              <span>Your information is held in strict confidence</span>
            </div>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-secondary via-card to-background border border-border/50 ${
        variant === 'modal' ? 'max-w-2xl mx-auto' : ''
      }`}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="relative z-10 p-6 md:p-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-gold flex items-center justify-center">
              <Crown className="h-5 w-5 text-cream" />
            </div>
            <div>
              <p className="text-sm font-medium text-gold uppercase tracking-wider">Private Assessment</p>
              <p className="text-xs text-muted-foreground">Confidential & Personalized</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" />
            <span>256-bit encrypted</span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>Step {showContactForm ? steps.length + 1 : currentStep + 1} of {steps.length + 1}</span>
            <span>{Math.round(showContactForm ? 100 : progress)}% complete</span>
          </div>
          <div className="h-1.5 bg-muted/30 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-gold rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${showContactForm ? 100 : progress}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!showContactForm ? (
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Question */}
              <div className="flex items-start gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <StepIcon className="h-6 w-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-display text-xl md:text-2xl text-foreground mb-1">
                    {currentStepData.question}
                  </h3>
                  <p className="text-sm text-muted-foreground">{currentStepData.subtitle}</p>
                </div>
              </div>

              {/* Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {currentStepData.options.map((option) => {
                  const isMultiSelect = multiSelectSteps.includes(currentStepData.id);
                  const isSelected = isMultiSelect
                    ? ((answers[currentStepData.id] as string[]) || []).includes(option.value)
                    : answers[currentStepData.id] === option.value;

                  return (
                    <motion.button
                      key={option.value}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleOptionSelect(currentStepData.id, option.value)}
                      className={`relative p-4 rounded-xl border text-left transition-all duration-300 ${
                        isSelected
                          ? 'border-gold bg-gold/10 shadow-lg shadow-gold/10'
                          : 'border-border/50 bg-background/50 hover:border-gold/50 hover:bg-muted/30'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className={`font-medium ${isSelected ? 'text-gold' : 'text-foreground'}`}>
                            {option.label}
                          </p>
                          {option.sublabel && (
                            <p className="text-xs text-muted-foreground mt-0.5">{option.sublabel}</p>
                          )}
                        </div>
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-5 h-5 rounded-full bg-gradient-gold flex items-center justify-center"
                          >
                            <CheckCircle className="h-3.5 w-3.5 text-cream" />
                          </motion.div>
                        )}
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className="text-muted-foreground"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>

                {multiSelectSteps.includes(currentStepData.id) && (
                  <Button
                    onClick={handleNext}
                    disabled={!answers[currentStepData.id] || (answers[currentStepData.id] as string[]).length === 0}
                    className="bg-gradient-gold text-cream hover:opacity-90"
                  >
                    Continue
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="contact-form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-start gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-display text-xl md:text-2xl text-foreground mb-1">
                    Receive Your Personalized Strategy
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    A senior advisor will contact you within 24 hours with tailored recommendations.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Full Name *</label>
                  <Input
                    type="text"
                    value={contactInfo.name}
                    onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                    placeholder="Your name"
                    className="h-12 bg-background/80 border-border"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email Address *</label>
                  <Input
                    type="email"
                    value={contactInfo.email}
                    onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                    placeholder="your@email.com"
                    className="h-12 bg-background/80 border-border"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Phone Number <span className="text-muted-foreground">(optional)</span>
                  </label>
                  <Input
                    type="tel"
                    value={contactInfo.phone}
                    onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    className="h-12 bg-background/80 border-border"
                  />
                </div>

                <div className="flex items-center justify-between pt-4">
                  <Button variant="ghost" type="button" onClick={handleBack} className="text-muted-foreground">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-gradient-gold text-cream hover:opacity-90 px-8"
                    size="lg"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Get My Strategy
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground text-center pt-2">
                  By submitting, you agree to our{' '}
                  <a href="/privacy" className="text-gold hover:underline">
                    Privacy Policy
                  </a>
                  . Your data is protected and never shared.
                </p>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default InvestmentAssessment;
